import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import { useTranslation } from "react-i18next";

import { invoiceQueryKeys } from "../api/queryKeys";

import { instance } from "@/lib/axios/instance";
import {
  handleProblemDetailsError,
  ProblemDetails,
} from "@/lib/helpers/handleProblemDetailsError";
import useToast from "@/lib/hooks/useToast";
import { BulkUpdateChannelInvoiceRequest } from "@/types/request/BulkUpdateChannelInvoiceRequest";
import { ChannelInvoiceType } from "@/types/response/ChannelInvoiceType";
import { PagedResponse } from "@/types/response/PagedResponse";

type UseBulkUpdateChannelInvoicesParams = {
  channelId: string;
  year: string;
  month: string;
  tenantId?: string | null;
};

/**
 * Updates multiple channel invoices in bulk for a specific channel, year, and month.
 * Automatically invalidates related queries and shows success/error toasts.
 */
export function useBulkUpdateChannelInvoices(
  params: UseBulkUpdateChannelInvoicesParams
) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { t } = useTranslation();

  return useMutation<
    AxiosResponse,
    AxiosError<ProblemDetails>,
    BulkUpdateChannelInvoiceRequest,
    { previousQueries: Array<{ queryKey: readonly unknown[]; data: unknown }> }
  >({
    mutationFn: (request: BulkUpdateChannelInvoiceRequest) =>
      instance
        .put<AxiosResponse>(
          `/api/v2/Channels/${params.channelId}/Invoices/${params.year}/${params.month}/Bulk`,
          {
            ...request,
            tenantId: params.tenantId,
          }
        )
        .then((res) => res.data),
    onMutate: async (request) => {
      // Cancel outgoing queries to avoid overwriting optimistic updates
      await queryClient.cancelQueries({
        queryKey: invoiceQueryKeys.channels.details(),
        exact: false,
      });

      // Snapshot previous values for rollback
      const previousQueries: Array<{
        queryKey: readonly unknown[];
        data: unknown;
      }> = [];

      // Capture all affected queries before updating
      const affectedQueries = queryClient
        .getQueryCache()
        .findAll({
          queryKey: invoiceQueryKeys.channels.details(),
          exact: false,
        });

      affectedQueries.forEach((query) => {
        if (query.state.data) {
          previousQueries.push({
            queryKey: query.queryKey,
            data: query.state.data,
          });
        }
      });

      // Optimistically update channel invoice queries (invoiced tab)
      queryClient.setQueriesData(
        {
          queryKey: invoiceQueryKeys.channels.details(),
          exact: false,
        },
        (old: PagedResponse<ChannelInvoiceType> | undefined) => {
          if (!old?.data) return old;

          // Update matching invoices optimistically
          const updatedData = old.data.map((item: ChannelInvoiceType) => {
            const update = request.invoices.find(
              (inv) =>
                inv.channelId === item.channelId &&
                inv.clientId2 === item.clientId2
            );
            if (update) {
              return { ...item, amount: update.amount };
            }
            return item;
          });

          return { ...old, data: updatedData };
        }
      );

      return { previousQueries };
    },
    onError: (error, _variables, context) => {
      // Rollback optimistic updates on error
      if (context?.previousQueries) {
        context.previousQueries.forEach(({ queryKey, data }) => {
          queryClient.setQueryData(queryKey, data);
        });
      }

      handleProblemDetailsError({
        error,
        t,
        showToast,
        fallbackMessage: t("errors.somethingWentWrong"),
      });
    },
    onSuccess: () => {
      // Mark the local query as stale WITHOUT triggering a refetch.
      // The optimistic update from onMutate is already showing the correct value.
      // An immediate refetch would overwrite it before the async event consumer updates the read model.
      queryClient.invalidateQueries({
        queryKey: invoiceQueryKeys.channels.details(),
        refetchType: "none",
      });
      queryClient.invalidateQueries({
        queryKey: invoiceQueryKeys.cache.channelMonths.byChannelYearMonth(
          params.channelId,
          params.year,
          params.month
        ),
        refetchType: "none",
      });
      // Cross-page queries: mark stale, refetchOnMount: true handles refresh on navigation
      queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.cache.clientBudgetMonth.all() });
      queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.cache.clientChannels.all() });
      queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.cache.channels.all() });
      queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.cache.clients.all() });
      queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.cache.transactions.all() });

      showToast(t("success.save", { entity: t("common.invoices") }));
    },
  });
}
